import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import DOMPurify from 'dompurify';

const BASE_URL = window.location.hostname === 'localhost' ? 'https://localhost:7000/api' : 'https://timcheh.ca/api';

const RulesPage: React.FC = () => {
  const { data: contentPage, isLoading, error } = useQuery({
    queryKey: ['content-page', 'Rules'],
    queryFn: async () => {
      try {
        const response = await axios.get(`${BASE_URL}/content-pages/Rules`);
        return response.data;
      } catch (error) {
        return null;
      }
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !contentPage) {
    return (
      <div className="min-h-screen bg-gray-50" dir="rtl">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                <li>
                  <Link to="/" className="text-gray-500 hover:text-gray-700 transition-colors">
                    خانه
                  </Link>
                </li>
                <li>
                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                </li>
                <li>
                  <span className="text-gray-900 font-medium">قوانین</span>
                </li>
              </ol>
            </nav>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h1 className="text-3xl font-semibold text-gray-900 mb-6">قوانین</h1>
            <p className="text-gray-600">محتوایی در حال حاضر در دسترس نیست.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="ltr">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              </li>
              <li>
                <span className="text-gray-900 font-medium">{contentPage.title || 'قوانین'}</span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 via-white to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-primary-200 to-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-200 to-pink-200 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center bg-gradient-to-r from-primary-500 to-blue-500 px-6 py-3 rounded-full mb-8 shadow-lg">
            <ShieldExclamationIcon className="h-5 w-5 text-white ml-2" />
            <span className="text-white font-semibold text-sm">قوانین پلتفرم</span>
          </div>
          
          <h1 className="text-6xl font-semibold text-gray-900 mb-8 leading-tight">
            {contentPage.title || 'قوانین و دستورالعمل‌ها'}
          </h1>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            لطفاً این قوانین را بررسی و رعایت کنید تا تجربه‌ای امن و مثبت برای همه کاربران در تیمچه تضمین شود.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8 lg:p-12">
            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contentPage.content) }}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default RulesPage;

